import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  AddBusiness as AddBusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  CSVRowData,
  CSVColumnMapping,
  BuildingMatchItem,
  BulkTeamImportItem,
  BulkTeamImportResultItem,
  MinimalBuilding,
} from '../../../../types';
import {
  parseCSV,
  validateCSVFile,
  extractBuildingNames,
  parseDomains,
  exportResultsToCSV,
  downloadCSV,
} from '../../../../utils/csvParser';
import { matchBuildings } from '../../../../services/api/endpoints/buildings';
import { bulkImportTeamsChunked } from '../../../../services/api/endpoints/caas';
import { searchBuildings } from '../../../../services/api/endpoints/buildings';
import BuildingFormModal from '../../../caas/components/buildings/BuildingFormModal';

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: string;
  clientName: string;
}

const STEPS = ['Upload CSV', 'Map Columns', 'Review Buildings', 'Import Results'];

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  open,
  onClose,
  onSuccess,
  clientId,
  clientName,
}) => {
  const { user } = useAuth();

  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Step 1: Upload
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<CSVRowData[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isParsingCSV, setIsParsingCSV] = useState(false);

  // Step 2: Column mapping
  const [columnMapping, setColumnMapping] = useState<CSVColumnMapping>({
    teamName: null,
    buildingName: null,
    domains: null,
  });

  // Step 3: Building review
  const [buildingMatches, setBuildingMatches] = useState<BuildingMatchItem[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<Record<number, string>>({});
  const [isMatchingBuildings, setIsMatchingBuildings] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [allBuildings, setAllBuildings] = useState<MinimalBuilding[]>([]);
  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState(false);

  // Step 4: Import results
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<BulkTeamImportResultItem[] | null>(null);
  const [importSummary, setImportSummary] = useState<{ total: number; successful: number; failed: number } | null>(null);
  const [resultsFilter, setResultsFilter] = useState<'all' | 'successful' | 'failed'>('all');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setUploadedFile(null);
      setCsvHeaders([]);
      setCsvRows([]);
      setUploadError(null);
      setColumnMapping({ teamName: null, buildingName: null, domains: null });
      setBuildingMatches([]);
      setSelectedBuildings({});
      setMatchError(null);
      setImportResults(null);
      setImportSummary(null);
      setImportProgress({ current: 0, total: 0 });
      setResultsFilter('all');
      setShowCloseConfirm(false);
    }
  }, [open]);

  // Handle file upload
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploadedFile(file);

    // Validate file
    const validation = validateCSVFile(file);
    if (!validation.valid) {
      setUploadError(validation.error || 'Invalid file');
      return;
    }

    // Parse CSV
    setIsParsingCSV(true);
    try {
      const result = await parseCSV(file);
      if (result.error) {
        setUploadError(result.error);
      } else {
        setCsvHeaders(result.headers);
        setCsvRows(result.rows);
      }
    } catch (error: any) {
      setUploadError(error.message || 'Failed to parse CSV');
    } finally {
      setIsParsingCSV(false);
    }
  };

  // Handle column mapping change
  const handleColumnMappingChange = (field: keyof CSVColumnMapping, value: string | null) => {
    setColumnMapping((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Validate column mapping
  const isColumnMappingValid = (): boolean => {
    if (!columnMapping.teamName || !columnMapping.buildingName) {
      return false;
    }
    // Check for duplicate mappings
    const values = [columnMapping.teamName, columnMapping.buildingName, columnMapping.domains].filter(Boolean);
    return new Set(values).size === values.length;
  };

  // Handle next step
  const handleNext = async () => {
    if (currentStep === 0) {
      // Moving from upload to column mapping
      setCurrentStep(1);
    } else if (currentStep === 1) {
      // Moving from column mapping to building review
      if (!user?.token || !columnMapping.buildingName) return;

      setIsMatchingBuildings(true);
      setMatchError(null);

      try {
        // Fetch all buildings
        const buildingsResponse = await searchBuildings(user.token, { page: 1, limit: 100 });
        setAllBuildings(buildingsResponse.items);
        
        // Get fuzzy matches for auto-selection
        const buildingNames = extractBuildingNames(csvRows, columnMapping.buildingName);
        const matchResponse = await matchBuildings(user.token, buildingNames);
        setBuildingMatches(matchResponse.matches);

        // Pre-populate selections for high confidence matches
        const initialSelections: Record<number, string> = {};
        csvRows.forEach((row) => {
          const buildingName = row[columnMapping.buildingName!];
          const match = matchResponse.matches.find((m) => m.input_name === buildingName);
          
          // Auto-select exact, high, and medium confidence matches
          // Only skip 'none' (no match found)
          if (match?.matched_building && match.confidence !== 'none') {
            initialSelections[row.rowIndex] = match.matched_building.id;
          }
        });
        
        setSelectedBuildings(initialSelections);
        setCurrentStep(2);
      } catch (error: any) {
        setMatchError(error.message || 'Failed to match buildings');
      } finally {
        setIsMatchingBuildings(false);
      }
    } else if (currentStep === 2) {
      // Moving from building review to import (perform import)
      await handleImport();
    }
  };

  // Handle back step
  const handleBack = () => {
    if (currentStep > 0 && currentStep < 3) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Get match for a building name
  const getMatchForRow = (row: CSVRowData): BuildingMatchItem | undefined => {
    if (!columnMapping.buildingName) return undefined;
    const buildingName = row[columnMapping.buildingName];
    return buildingMatches.find((m) => m.input_name === buildingName);
  };

  // Get confidence badge
  const getConfidenceBadge = (confidence: string | null) => {
    switch (confidence) {
      case 'exact':
        return <Chip label="Exact Match" color="success" size="small" />;
      case 'starts_with':
        return <Chip label="High Confidence" color="warning" size="small" />;
      case 'contains':
        return <Chip label="Low Confidence - Please Review" color="warning" size="small" sx={{ bgcolor: 'orange' }} />;
      case 'none':
        return <Chip label="No Match - Please Select" color="error" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };


  // Handle building selection for a row
  const handleBuildingSelect = (rowIndex: number, buildingId: string | null) => {
    setSelectedBuildings((prev) => {
      const updated = { ...prev };
      if (buildingId) {
        updated[rowIndex] = buildingId;
      } else {
        delete updated[rowIndex];
      }
      return updated;
    });
  };

  // Check if all rows have buildings selected
  const areAllBuildingsSelected = (): boolean => {
    return csvRows.every((row) => selectedBuildings[row.rowIndex]);
  };

  // Handle building creation
  const handleBuildingCreated = async (buildingId: string) => {
    setIsBuildingModalOpen(false);
    
    if (!user?.token) return;
    
    // Fetch the newly created building and add it to the list
    try {
      const response = await searchBuildings(user.token, { page: 1, limit: 100 });
      const newBuilding = response.items.find((b) => b.id === buildingId);
      
      if (newBuilding) {
        // Add the new building to the allBuildings list
        setAllBuildings((prev) => [newBuilding, ...prev]);
      }
    } catch (error) {
      console.error('Error fetching new building:', error);
    }
  };

  // Handle import with chunking and progress tracking
  const handleImport = async () => {
    if (!user?.token || !columnMapping.teamName || !columnMapping.buildingName) return;

    setIsImporting(true);
    setImportProgress({ current: 0, total: 0 });

    try {
      // Build import payload
      const teams: BulkTeamImportItem[] = csvRows.map((row) => {
        const domainsValue = columnMapping.domains ? row[columnMapping.domains] : null;
        
        return {
          name: row[columnMapping.teamName!],
          building_id: selectedBuildings[row.rowIndex],
          domains: parseDomains(domainsValue),
        };
      });

      // Call chunked bulk import API with progress callback
      const response = await bulkImportTeamsChunked(
        user.token,
        clientId,
        teams,
        (current, total, partialResults) => {
          // Update progress as chunks complete
          setImportProgress({ current, total });
          // Optionally update results in real-time
          // setImportResults(partialResults);
        }
      );

      setImportResults(response.results);
      setImportSummary({
        total: response.total,
        successful: response.successful,
        failed: response.failed,
      });

      setCurrentStep(3);
    } catch (error: any) {
      setMatchError(error.message || 'Failed to import teams');
    } finally {
      setIsImporting(false);
    }
  };

  // Handle download results
  const handleDownloadResults = () => {
    if (!importResults) return;
    
    const csv = exportResultsToCSV(importResults);
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `team-import-results-${timestamp}.csv`);
  };

  // Handle done
  const handleDone = () => {
    onSuccess();
  };

  // Handle close with confirmation
  const handleCloseClick = () => {
    // If we're in the middle of the flow (uploaded data but not done), confirm
    if (currentStep > 0 && currentStep < 3 && !isImporting) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  // Get filtered results
  const getFilteredResults = (): BulkTeamImportResultItem[] => {
    if (!importResults) return [];
    
    switch (resultsFilter) {
      case 'successful':
        return importResults.filter((r) => r.success);
      case 'failed':
        return importResults.filter((r) => !r.success);
      default:
        return importResults;
    }
  };

  // Determine if Next button should be disabled
  const isNextDisabled = (): boolean => {
    if (currentStep === 0) {
      return !csvRows.length || isParsingCSV;
    }
    if (currentStep === 1) {
      return !isColumnMappingValid() || isMatchingBuildings;
    }
    if (currentStep === 2) {
      return !areAllBuildingsSelected() || isImporting;
    }
    return false;
  };

  // Get modal width based on step
  const getModalWidth = () => {
    if (currentStep === 0 || currentStep === 1) {
      return { xs: '95%', sm: '90%', md: '600px' };
    }
    return { xs: '95%', sm: '95%', md: '1000px', lg: '1200px' };
  };

  return (
    <>
      <Modal open={open} onClose={handleCloseClick} aria-labelledby="bulk-import-modal-title">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: getModalWidth(),
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography id="bulk-import-modal-title" variant="h6" component="h2">
              Import Teams for {clientName}
            </Typography>
            <IconButton onClick={handleCloseClick} size="small" disabled={isImporting}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Stepper */}
          <Box sx={{ px: 3, pt: 3 }}>
            <Stepper activeStep={currentStep}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
            {/* Step 1: Upload CSV */}
            {currentStep === 0 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Upload a CSV file with team information. Maximum 500 teams per import.
                </Typography>

                <Box
                  sx={{
                    border: 2,
                    borderColor: uploadError ? 'error.main' : 'divider',
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    mb: 3,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Button variant="contained" component="label" disabled={isParsingCSV}>
                    {isParsingCSV ? 'Parsing...' : 'Choose CSV File'}
                    <input type="file" accept=".csv" hidden onChange={handleFileChange} />
                  </Button>
                  {uploadedFile && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      {uploadedFile.name}
                    </Typography>
                  )}
                </Box>

                {uploadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {uploadError}
                  </Alert>
                )}

                {csvRows.length > 0 && (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Found {csvRows.length} teams
                    </Alert>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Preview (first 5 rows):
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {csvHeaders.map((header) => (
                              <TableCell key={header}>{header}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {csvRows.slice(0, 5).map((row, idx) => (
                            <TableRow key={idx}>
                              {csvHeaders.map((header) => (
                                <TableCell key={header}>{row[header] || '—'}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 2: Column Mapping */}
            {currentStep === 1 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Tell us which columns contain which data
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Team Name Column *</InputLabel>
                  <Select
                    value={columnMapping.teamName || ''}
                    label="Team Name Column *"
                    onChange={(e) => handleColumnMappingChange('teamName', e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>Select column</em>
                    </MenuItem>
                    {csvHeaders.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                    The name of each team
                  </Typography>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Building Name Column *</InputLabel>
                  <Select
                    value={columnMapping.buildingName || ''}
                    label="Building Name Column *"
                    onChange={(e) => handleColumnMappingChange('buildingName', e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>Select column</em>
                    </MenuItem>
                    {csvHeaders.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                    The building each team is located in
                  </Typography>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Domains Column (Optional)</InputLabel>
                  <Select
                    value={columnMapping.domains || ''}
                    label="Domains Column (Optional)"
                    onChange={(e) => handleColumnMappingChange('domains', e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {csvHeaders.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                    Email domains for auto-assignment (comma-separated in CSV)
                  </Typography>
                </FormControl>
              </Box>
            )}

            {/* Step 3: Building Review */}
            {currentStep === 2 && (
              <Box>
                {/* Show progress bar when importing */}
                {isImporting && importProgress.total > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Processing batch {importProgress.current} of {importProgress.total}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round((importProgress.current / importProgress.total) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(importProgress.current / importProgress.total) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1">
                    Review and match buildings. {areAllBuildingsSelected() ? 'All teams have buildings selected.' : `${csvRows.length - Object.keys(selectedBuildings).length} teams need buildings.`}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddBusinessIcon />}
                    onClick={() => setIsBuildingModalOpen(true)}
                    size="small"
                    disabled={isImporting}
                  >
                    Create Building
                  </Button>
                </Box>

                {matchError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {matchError}
                  </Alert>
                )}

                {!areAllBuildingsSelected() && !isImporting && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    All teams must have a building selected before importing
                  </Alert>
                )}

                <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Team Name</TableCell>
                        <TableCell>Building from CSV</TableCell>
                        <TableCell>Matched Building</TableCell>
                        <TableCell>Confidence</TableCell>
                        {columnMapping.domains && <TableCell>Domains</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvRows.map((row) => {
                        const match = getMatchForRow(row);
                        const rowIndex = row.rowIndex;
                        const selectedBuildingId = selectedBuildings[rowIndex];
                        const selectedBuilding = allBuildings.find(b => b.id === selectedBuildingId);

                        return (
                          <TableRow key={rowIndex}>
                            <TableCell>{rowIndex + 1}</TableCell>
                            <TableCell>{columnMapping.teamName ? row[columnMapping.teamName] : '—'}</TableCell>
                            <TableCell>{columnMapping.buildingName ? row[columnMapping.buildingName] : '—'}</TableCell>
                            <TableCell sx={{ minWidth: 250 }}>
                              <Autocomplete
                                size="small"
                                options={allBuildings}
                                getOptionLabel={(option) => `${option.name} - ${option.address}`}
                                value={selectedBuilding || null}
                                onChange={(_, newValue) => handleBuildingSelect(rowIndex, newValue?.id || null)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select building..."
                                    error={!selectedBuildingId}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>{getConfidenceBadge(match?.confidence || null)}</TableCell>
                            {columnMapping.domains && (
                              <TableCell>
                                {row[columnMapping.domains] ? (
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                    {row[columnMapping.domains]}
                                  </Typography>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Step 4: Results */}
            {currentStep === 3 && importResults && importSummary && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Import Complete
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h4">{importSummary.total}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.light' }}>
                    <Typography variant="body2" color="success.dark">
                      Successful
                    </Typography>
                    <Typography variant="h4" color="success.dark">
                      {importSummary.successful}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'error.light' }}>
                    <Typography variant="body2" color="error.dark">
                      Failed
                    </Typography>
                    <Typography variant="h4" color="error.dark">
                      {importSummary.failed}
                    </Typography>
                  </Paper>
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={resultsFilter} onChange={(_, value) => setResultsFilter(value)}>
                    <Tab label={`All (${importResults.length})`} value="all" />
                    <Tab label={`Successful (${importSummary.successful})`} value="successful" />
                    <Tab label={`Failed (${importSummary.failed})`} value="failed" />
                  </Tabs>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Team Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredResults().map((result) => (
                        <TableRow key={result.row_index}>
                          <TableCell>{result.row_index + 1}</TableCell>
                          <TableCell>{result.team_name}</TableCell>
                          <TableCell>
                            {result.success ? (
                              <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
                            ) : (
                              <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {result.success ? (
                              <Typography variant="body2" color="text.secondary">
                                Team ID: {result.team_id}
                              </Typography>
                            ) : (
                              <Box>
                                <Typography variant="body2" color="error">
                                  {result.error}
                                </Typography>
                                {result.error_detail && (
                                  <Typography variant="caption" color="text.secondary" title={result.error_detail}>
                                    {result.error_detail}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>

          {/* Footer with action buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              {currentStep === 3 && importResults && (
                <Button startIcon={<DownloadIcon />} onClick={handleDownloadResults}>
                  Download Results
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {currentStep > 0 && currentStep < 3 && (
                <Button onClick={handleBack} disabled={isImporting || isMatchingBuildings}>
                  Back
                </Button>
              )}
              {currentStep < 2 && (
                <Button variant="contained" onClick={handleNext} disabled={isNextDisabled()}>
                  {isMatchingBuildings ? <CircularProgress size={24} /> : 'Next'}
                </Button>
              )}
              {currentStep === 2 && (
                <Button variant="contained" onClick={handleNext} disabled={isNextDisabled()}>
                  {isImporting ? <CircularProgress size={24} /> : 'Import'}
                </Button>
              )}
              {currentStep === 3 && (
                <Button variant="contained" onClick={handleDone}>
                  Done
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Building Creation Modal */}
      <BuildingFormModal
        open={isBuildingModalOpen}
        onClose={() => setIsBuildingModalOpen(false)}
        onSuccess={handleBuildingCreated}
      />

      {/* Close confirmation dialog */}
      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)}>
        <DialogTitle>Close Import?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have uploaded data but haven't completed the import. Are you sure you want to close? Your progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirm(false)}>Cancel</Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkImportModal;
